import { createAsyncThunk } from "@reduxjs/toolkit";
import { types, toast } from "@vilocnv/allsetra-core";
import MileageDeviationsService from "app/data/services/MileageDeviationsService";
import i18n from "i18next";

export const getAllMileageDeviationsThunk = createAsyncThunk(
  "mileageDeviations/getAllMileageDeviationsThunk",
  async ({
    params,
    accountId
  }: {
    params: types.IRecordsAggregationBody;
    accountId: string;
  }) => {
    try {
      const response =
        await MileageDeviationsService.getMileageDeviationsByQuery(
          params,
          accountId
        );

      if (response.status === 200) {
        return response.data;
      }

      return { results: [], rowCount: 0 };
    } catch (e: unknown) {
      console.error(e);
      toast.error(i18n.t("errors.generic.unknownError"));
      return { results: [], rowCount: 0 };
    }
  }
);

export const updateMileageDeviationStatusThunk = createAsyncThunk(
  "mileageDeviations/updateMileageDeviationStatusThunk",
  async ({
    accountId,
    objectId,
    deviationId,
    status,
    comment,
    userEmail
  }: {
    accountId: string;
    objectId: string;
    deviationId: string;
    status: number;
    comment: string;
    userEmail?: string;
  }) => {
    try {
      const response =
        await MileageDeviationsService.updateMileageDeviationStatus({
          accountId,
          objectId,
          deviationId,
          data: { status, comment }
        });

      if (response.status === 200 || response.status === 202) {
        toast.success(i18n.t("toasts.statusUpdatedSuccess"));
        return {
          deviationId,
          status,
          comment,
          userEmail,
          data: response.data
        };
      } else {
        toast.error(i18n.t("toasts.statusUpdateError"));
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (e: unknown) {
      toast.error(i18n.t("toasts.statusUpdateErrorRetry"));
      throw new Error(String(e));
    }
  }
);

export const updateMileageDeviationStatusOnlyThunk = createAsyncThunk(
  "mileageDeviations/updateMileageDeviationStatusOnlyThunk",
  async ({
    accountId,
    objectId,
    deviationId,
    status,
    userEmail
  }: {
    accountId: string;
    objectId: string;
    deviationId: string;
    status: number;
    userEmail?: string;
  }) => {
    try {
      const response =
        await MileageDeviationsService.updateMileageDeviationStatusOnly({
          accountId,
          objectId,
          deviationId,
          data: { status }
        });

      if (response.status === 200 || response.status === 202) {
        toast.success(i18n.t("toasts.statusUpdatedSuccess"));
        return {
          deviationId,
          status,
          userEmail,
          data: response.data
        };
      } else {
        toast.error(i18n.t("toasts.statusUpdateError"));
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (e: unknown) {
      toast.error(i18n.t("toasts.statusUpdateErrorRetry"));
      throw new Error(String(e));
    }
  }
);

export const updateMileageDeviationCommentOnlyThunk = createAsyncThunk(
  "mileageDeviations/updateMileageDeviationCommentOnlyThunk",
  async ({
    accountId,
    objectId,
    deviationId,
    comment,
    userEmail
  }: {
    accountId: string;
    objectId: string;
    deviationId: string;
    comment: string;
    userEmail?: string;
  }) => {
    try {
      const response =
        await MileageDeviationsService.updateMileageDeviationCommentOnly({
          accountId,
          objectId,
          deviationId,
          data: { comment }
        });

      if (response.status === 200 || response.status === 202) {
        toast.success(i18n.t("toasts.commentUpdatedSuccess"));
        return {
          deviationId,
          comment,
          userEmail,
          data: response.data
        };
      } else {
        toast.error(i18n.t("toasts.commentUpdateError"));
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (e: unknown) {
      toast.error(i18n.t("toasts.commentUpdateErrorRetry"));
      throw new Error(String(e));
    }
  }
);

export const updateMileageDeviationCommentThunk = createAsyncThunk(
  "mileageDeviations/updateMileageDeviationCommentThunk",
  async ({
    accountId,
    objectId,
    deviationId,
    currentStatus,
    comment,
    userEmail
  }: {
    accountId: string;
    objectId: string;
    deviationId: string;
    currentStatus: number;
    comment: string;
    userEmail?: string;
  }) => {
    try {
      const response =
        await MileageDeviationsService.updateMileageDeviationStatus({
          accountId,
          objectId,
          deviationId,
          data: { status: currentStatus, comment }
        });

      if (response.status === 200 || response.status === 202) {
        toast.success(i18n.t("toasts.commentUpdatedSuccess"));
        return {
          deviationId,
          status: currentStatus,
          comment,
          userEmail,
          data: response.data
        };
      } else {
        toast.error(i18n.t("toasts.commentUpdateError"));
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (e: unknown) {
      toast.error(i18n.t("toasts.commentUpdateErrorRetry"));
      throw new Error(String(e));
    }
  }
);
